import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBillingInvoice } from '../../../hooks/useBilling';
import { useToast } from '../../../providers/ToastProvider';
import type { BillingInvoiceDetail } from '../../../services/billing';
import { formatMoneyDollars as money } from '../../../utils/money';
import { chargeMilestoneLabel } from '../shared/milestones';
import { AmountRow, Drawer, DrawerSection, KVRow } from '../shared/Drawer';
import { PayButton } from '../shared/PayButton';
import { StatusBadge } from '../shared/StatusBadge';

const INVOICE_NOT_FOUND_MESSAGE = 'Invoice not found';

function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

interface InvoiceDrawerProps {
    invoiceId: string;
    onClose: () => void;
    closeDisabled?: boolean;
}

// Visual language ported from files-viewer's ChargeDrawer.tsx: Drawer/
// DrawerSection/KVRow chrome (see ../shared/Drawer.tsx), a Summary section of
// KVRows, and an AmountRow-based breakdown (Line items total -> Discount ->
// Charge total -> credits -> Invoice total). Kept doctor-facing only — no
// Activity/audit, purchase orders, or patient link/unlink like the admin
// drawer has. The Pay CTA (this app's own addition — ChargeDrawer has no
// payment flow) is passed via the ported Drawer's `footer` slot, a deliberate
// addition beyond the source component so it stays pinned below the
// scrollable body instead of scrolling away with the last section.
export const InvoiceDrawer: React.FC<InvoiceDrawerProps> = ({
    invoiceId,
    onClose,
    closeDisabled
}) => {
    const { data: invoice, isLoading, isError } = useBillingInvoice(invoiceId);
    const { showToast } = useToast();

    // Deep-link/refresh 404 handling: the ?invoice= param may point at an
    // invoice that doesn't exist or doesn't belong to this practice (the
    // backend 404s the same way for both, see mine.invoices.service.ts's
    // membership-checked notFound()). Drop the param silently (onClose,
    // wired by InvoicesSection to clear it) and explain via toast, rather
    // than leaving a broken drawer open on a stale/bad link.
    useEffect(() => {
        if (!isError) return;
        showToast(INVOICE_NOT_FOUND_MESSAGE, 'error');
        onClose();
        // onClose intentionally omitted — it's a fresh callback identity per
        // InvoicesSection render (wraps setSearchParams) and re-running this
        // effect on that alone would refire the toast/close for no reason.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError, showToast]);

    if (isError) {
        // Nothing to show — the effect above is already closing the drawer.
        return null;
    }

    if (isLoading || !invoice) {
        return (
            <Drawer title="Invoice" onClose={onClose} closeDisabled={closeDisabled}>
                <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                    <span className="text-sm">Loading invoice…</span>
                </div>
            </Drawer>
        );
    }

    return (
        <InvoiceDrawerContent invoice={invoice} onClose={onClose} closeDisabled={closeDisabled} />
    );
};

interface InvoiceDrawerContentProps {
    invoice: BillingInvoiceDetail;
    onClose: () => void;
    closeDisabled?: boolean;
}

function lineItemsTotal(invoice: BillingInvoiceDetail): number {
    return invoice.lineItems.reduce((sum, item) => sum + Number(item.lineTotal), 0);
}

function creditsTotal(invoice: BillingInvoiceDetail): number {
    return invoice.creditsApplied.reduce((sum, credit) => sum + Number(credit.amount), 0);
}

const InvoiceDrawerContent: React.FC<InvoiceDrawerContentProps> = ({
    invoice,
    onClose,
    closeDisabled
}) => {
    const milestone = chargeMilestoneLabel(invoice.charge);
    const hasDiscount = invoice.discountType && invoice.discountValue;
    const netInvoiceTotal = Math.max(0, Number(invoice.charge.totalAmount) - creditsTotal(invoice));
    const isPayable = ['open', 'partial', 'overdue'].includes(invoice.derivedStatus);
    const isPaid = invoice.derivedStatus === 'paid';

    let footer: React.ReactNode;
    if (isPayable) {
        footer = (
            <>
                <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-700">Balance due now</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">
                        {money(invoice.amount)}
                    </span>
                </div>
                <PayButton
                    invoiceId={invoice.id}
                    derivedStatus={invoice.derivedStatus}
                    variant="drawer"
                />
                <p className="text-xs text-gray-400 text-center mt-2">
                    Opens our secure payment page in a new tab.
                </p>
            </>
        );
    } else if (isPaid) {
        // Reuses PayButton's paid-variant rendering (quiet "View invoice"
        // secondary button) — same pay-link mutation, Bill.com document
        // instead of a payment page.
        footer = (
            <>
                <PayButton
                    invoiceId={invoice.id}
                    derivedStatus={invoice.derivedStatus}
                    variant="drawer"
                />
                <p className="text-xs text-gray-400 text-center mt-2">
                    Opens the invoice document in a new tab.
                </p>
            </>
        );
    } else {
        footer = (
            <button
                type="button"
                onClick={onClose}
                className="w-full bg-white text-gray-800 border border-gray-300 px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
            >
                Close
            </button>
        );
    }

    return (
        <Drawer
            title={invoice.invoiceNumber}
            badge={<StatusBadge status={invoice.derivedStatus} />}
            subtitle={invoice.charge.productSummary ?? invoice.charge.description ?? milestone}
            onClose={onClose}
            closeDisabled={closeDisabled}
            footer={footer}
        >
            <DrawerSection title="Summary">
                <div className="flex flex-col">
                    <KVRow k="Patient">
                        {invoice.charge.patientId ? (
                            <Link
                                to={`/patient/${invoice.charge.patientId}/overview`}
                                className="hover:underline hover:text-gray-900"
                            >
                                {invoice.charge.patientName ?? 'Unknown patient'}
                            </Link>
                        ) : (
                            (invoice.charge.patientName ?? 'Unknown patient')
                        )}
                    </KVRow>
                    <KVRow k="Milestone">{milestone}</KVRow>
                    <KVRow k="Installment">
                        {invoice.installmentIndex + 1} of {invoice.installmentCount}
                    </KVRow>
                    <KVRow k="Due date">{fmtDate(invoice.dueDate)}</KVRow>
                    {invoice.paidAt && <KVRow k="Paid">{fmtDate(invoice.paidAt)}</KVRow>}
                </div>
            </DrawerSection>

            <DrawerSection title="What this covers">
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {invoice.lineItems.map((item, index) => (
                        <div
                            key={`${item.description}-${index}`}
                            className="flex items-start justify-between gap-4 px-4 py-3"
                        >
                            <p className="text-sm font-medium text-gray-800 min-w-0 truncate">
                                {item.description}
                            </p>
                            <div className="text-right shrink-0">
                                <p className="text-sm font-semibold text-gray-900 tabular-nums">
                                    {money(item.lineTotal)}
                                </p>
                                {item.quantity > 1 && (
                                    <p className="text-xs text-gray-400">
                                        {item.quantity} × {money(item.unitPrice)}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </DrawerSection>

            <DrawerSection title="Amount">
                <div className="border border-gray-200 rounded-lg p-4 flex flex-col">
                    <AmountRow label="Line items total" value={money(lineItemsTotal(invoice))} />
                    {hasDiscount && (
                        <AmountRow
                            label="Discount"
                            sub={invoice.discountType ?? undefined}
                            value={`−${invoice.discountValue}${invoice.discountType === 'percentage' ? '%' : ''}`}
                            minus
                        />
                    )}
                    <AmountRow
                        label="Charge total"
                        value={money(invoice.charge.totalAmount)}
                        strong
                    />
                    {invoice.creditsApplied.map((credit, index) => (
                        <AmountRow
                            key={`${credit.description}-${index}`}
                            label="Credit applied"
                            sub={credit.description}
                            value={`−${money(credit.amount)}`}
                            minus
                        />
                    ))}
                    <AmountRow label="Invoice total" value={money(netInvoiceTotal)} strong />
                </div>
            </DrawerSection>

            {invoice.schedule.length > 1 && (
                <DrawerSection title="Installment schedule">
                    <div className="flex flex-col gap-2">
                        {invoice.schedule.map((item) => {
                            const isCurrent = item.installmentIndex === invoice.installmentIndex;
                            return (
                                <div
                                    key={item.installmentIndex}
                                    className={`flex items-center gap-3 rounded-lg px-3 py-2 border ${
                                        isCurrent
                                            ? 'bg-yellow-50 border-yellow-300'
                                            : 'bg-white border-gray-200'
                                    }`}
                                >
                                    <span
                                        className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${
                                            isCurrent
                                                ? 'bg-yellow-400 text-gray-900'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}
                                    >
                                        {item.installmentIndex + 1}
                                    </span>
                                    <span className="flex-1 text-sm text-gray-700">
                                        {money(item.amount)} due {fmtDate(item.dueDate)}
                                    </span>
                                    <StatusBadge status={item.derivedStatus} />
                                </div>
                            );
                        })}
                    </div>
                </DrawerSection>
            )}

            {invoice.derivedStatus === 'overdue' && invoice.daysLate !== null && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                    This invoice is {invoice.daysLate} day{invoice.daysLate === 1 ? '' : 's'} past
                    due.
                </div>
            )}
            {invoice.derivedStatus === 'partial' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
                    This invoice has been partially paid.
                </div>
            )}
        </Drawer>
    );
};
